import { Component, OnInit, ViewChild } from '@angular/core';
import { CustomerModel } from '@core/models/customer.model';
import { FormInputBase } from '@shared/components/dynamic-form/models/form-input-base';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { propostalForm } from './config/sendProposalForm';
import { PaymentAgreementService } from '@modules/payment-agreement/services/payment-agreement.service';
import { CustomerService } from '@modules/customers/customer.service';
import { ToastService } from '@services/ui';
import { CustomerCaseWorkflowI } from '@shared/components/send-propose-payment-agreement/domain/customerCaseWorkflowInstance';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFormComponent } from '@shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-send-propose-payment-agreement-modal',
  templateUrl: './send-propose-payment-agreement-modal.component.html',
})
export class SendProposePaymentAgreementModalComponent implements OnInit {
  @ViewChild('form') form!: DynamicFormComponent;

  propostalForm: FormInputBase<string | boolean>[] = propostalForm;
  customer: CustomerModel = {} as CustomerModel;
  customerCase: CustomerCaseWorkflowI = {} as CustomerCaseWorkflowI;
  sms: string = '';
  email: string = '';
  nMonths = 1;
  totalAmount = 0;
  installments: any = [];
  methods = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private customerService: CustomerService,
    private paymentAgreementService: PaymentAgreementService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initVariables();
    this.initInstalments();
  }

  initVariables() {
    this.customerCase = this.config.data;

    this.customerService.getCustomer(this.customerCase.customerId).then((res: any) => {
      this.customer = res;
      this.initForm();
    });
  }

  initInstalments() {
    this.paymentAgreementService
      .calculatePaymentAgreementInstalments(
        this.customerCase.customerCaseId || this.customerCase.id,
        1,
      )
      .subscribe((response) => {
        if (response.errors) {
          this.toast.showError('Error', response.errors[0].message);
          return;
        }
        this.installments = response;
      });
  }

  initForm() {
    this.propostalForm?.find((i) => i.key === 'channel')?.setOptions(this.setpropostalForms());
  }

  setpropostalForms() {
    const conditions: Record<string, (customer: CustomerModel) => boolean> = {
      sms: (customer) => (customer && customer.phones && customer.phones.length > 0) || false,
      email: (customer) =>
        (customer && customer.emailAddresses && customer.emailAddresses.length > 0) || false,
    };

    return this.methods.filter((method) => conditions[method.value]?.(this.customer));
  }

  onChanges(data: any) {
    this.sms = '';
    this.email = '';

    const { channel, instalments } = data;
    this.nMonths = instalments;

    this.handleChannel(channel);

    if (instalments) {
      this.paymentAgreementService
        .calculatePaymentAgreementInstalments(
          this.customerCase.customerCaseId || this.customerCase.id,
          instalments,
        )
        .subscribe((response) => {
          if (response.errors) {
            this.toast.showError('Error', response.errors[0].message);
            return;
          }
          this.installments = response;
        });
    }
  }

  private handleChannel(channelName: string): void {
    switch (channelName) {
      case 'sms':
        this.setFirstPhoneIfAvailable();
        break;
      case 'email':
        this.setFirstEmailIfAvailable();
        break;
    }
  }

  private setFirstPhoneIfAvailable(): void {
    if (this.customer && Array.isArray(this.customer.phones) && this.customer.phones.length > 0) {
      this.sms = this.customer.phones[0];
    }
  }

  private setFirstEmailIfAvailable(): void {
    if (
      this.customer &&
      Array.isArray(this.customer.emailAddresses) &&
      this.customer.emailAddresses.length > 0
    ) {
      this.email = this.customer.emailAddresses[0];
    }
  }

  checkDisabled() {
    return (!this.sms && !this.email) || !this.nMonths || !this.customerCase;
  }

  send() {
    const expirationControl = this.form.form.get('expirationAt');

    this.ref.close({
      channel: this.sms ? 'sms' : 'email',
      customerCase: this.customerCase,
      nMonths: this.nMonths,
      expirationAt: expirationControl?.value,
    });
  }

  getMonthText(nMonths: number): string {
    return nMonths === 1
      ? this.translate.instant('DATA.MODULES.SHARED.COMPONENTS.SEND_REMINDER.MONTH')
      : this.translate.instant('DATA.MODULES.SHARED.COMPONENTS.SEND_REMINDER.MONTHS');
  }

  get expirationDays(): number {
    if (!this.form || !this.form.form) return 0;

    const control = this.form.form.get('expirationAt');
    if (!control || !control.value) return 0;

    const selectedDate = new Date(control.value);
    const now = new Date();

    const selected = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return (selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  }
}
